import * as React from 'react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Loader, Progress, Dimmer, Button } from 'semantic-ui-react';
import IdleTimer from 'react-idle-timer';
import { DataRequest } from './request';
import { OAuthKey, refreshBeforeExpiry } from './oauth-key';
const qs = require('qs');
const _ = require('lodash');

const defaultIdleTimeout = 15 * 60000;  // 15 minutes
const retryAttempts = 10;
const accessTokenUrl = '/oauth/access';
const accessResetUrl = '/oauth/user/reset';

export interface LicenseFeatures {
    VOD: boolean;
    VODOnly: boolean;
    DAI: boolean;
    QoE: boolean;
    Capture: boolean;
    DataExtract: boolean;
    StreamPack: boolean;
    level: string;
    Licensed: boolean;
}

const DefaultLicenseFeatures: LicenseFeatures = {
    VOD: false,
    VODOnly: false,
    DAI: false,
    QoE: false,
    Capture: false,
    DataExtract: false,
    StreamPack: false,
    level: 'admin',
    Licensed: false
};

export interface VodStatus {
    enabled: boolean;
    active: boolean;
    mode: 'disabled' | 'continuous' | 'scheduled';
    streams: number;
    completed: number;
    cycles: number;
}

export interface SubSystemStatus {
    status: string;
    group: string;
    active: boolean;
    count: number;
}

export interface SabrSysInfo {
    name?: string;
    tzoffset?: number;
    tz?: string;
    version?: string;
    location?: string;
    installDate?: string;
    apiVersion?: number;
    tuneCount?: number;
    interface: 'live' | 'vod';
    scanning: SubSystemStatus;
    schedule: SubSystemStatus;
    vod: VodStatus;
    activeStreams?: number;
    activePlaylists?: number;
    licenseFlowLimit?: number;
    uptime?: number;
}

const BlankSabrSysInfo: SabrSysInfo = {
    interface: 'live',
    scanning: { status: '', group: '', active: false, count: 0 },
    schedule: { status: '', group: '', active: false, count: 0 },
    vod: {
        enabled: false,
        active: false,
        mode: 'disabled',
        streams: 0,
        completed: 0,
        cycles: 0
    }
};

export interface WaitforOptions {
    message ?: string;
    maxAttempts?: number;
    initialDelay?: number;  // in milliseconds
    checkInterval?: number;  // in milliseconds
}

const DefaultWaitforOptions: WaitforOptions = {
    maxAttempts: 30,
    checkInterval: 5000,        // how often we check for server recovery
    initialDelay: 1000          // how long to delay the first recovery test (after which checkInterval is used)
};

export class OAuthClientStatus {
    host: string;
    secureHost: boolean;
    clientID?: string;
    clientSecret?: string;
    clientNew?: string;
    scope?: string;
    key: OAuthKey;
    authenticated: boolean;
    VODLicensePresent: boolean;
    VODOnlyLicensePresent: boolean;
    DAILicensePresent: boolean;
    QoELicensePresent: boolean;
    Licensed: boolean;
    features?: LicenseFeatures;
    sys: SabrSysInfo;
    loginError?: string;
    level: string;
    ldapEnabled: boolean;

    private owner?: OAuthClient;

    constructor(_owner?: OAuthClient) {
        this.owner = _owner;
        this.host = 'localhost';
        this.secureHost = true;
        this.authenticated = false;
        this.VODLicensePresent = false;
        this.VODOnlyLicensePresent = false;
        this.DAILicensePresent = false;
        this.QoELicensePresent = false;
        this.Licensed = false;
        this.level = 'admin';   // todo: default user level should be public?
        this.ldapEnabled = false;
    }

    resolveServerUrl(url: string) {
        if (url.startsWith('/'))
            url = url.substr(1);
        return (this.secureHost ? 'https' : 'http') + '://' + this.host + '/' + url;
    }

    signInUsingClientCredentials(username: string, password: string) {
        if (this.owner)
            return this.owner.signInUsingClientCredentials(username, password);
        else
            return new Promise((resolve, reject) => { reject(null); });
    }

    updateUserPwd(username: string, password: string, newPwd: string) {
        if (this.owner)
            return this.owner.updateUserPwd(username, password, newPwd);
        else
            return new Promise((resolve, reject) => { reject(null); });
    }

    signInUsingNewCredentials(username: string, password: string, newPassword: string) {
        if (this.owner)
            return this.owner.signInUsingNewCredentials(username, password, newPassword);
        else
            return new Promise((resolve, reject) => { reject(null); });
    }

    signOut() {
        // window.localStorage.setItem('group-set', JSON.stringify({'set': ['Summary']}));     // resetting census screen group selections
        if (this.owner)
            this.owner.signOut();
        else
            this.key.expire();
    }

}

const OAuthContext = React.createContext<OAuthClientStatus>( new OAuthClientStatus() );

const OAuthScope = {
    WebUser: 'WebUser',             // indicates a human user. Also sets Status, Control and Configure scopes by default.
    Status: 'Status',               // can query status of objects
    Control: 'Control',             // can control objects and their activity
    Configure: 'Configure',         // can add, delete, and modify configuration of objects
    Admin: 'Admin',                 // can modify the system configuration but cannot modify user accounts
    Accounts: 'Accounts',           // can add/modify/delete user accounts
    Performance: 'Performance'
};

export interface OAuthCredentials {
    host?: string;
    secureHost: boolean;
    clientID: string;
    clientSecret: string;
    clientNew: string;
    scope?: string;
}

export class OAuthError {
    message: string;
}

export interface ApiRequestContext {
    oauth?: OAuthClient;
    key?: OAuthKey;
}

/* This object gets added to API requests within the request interceptor and can hold some private data for us.
 */
class ApiRequestSettings implements ApiRequestContext {
    oauth: OAuthClient;
    key: OAuthKey;
    loaderMessage?: string;

    public static from(config: AxiosRequestConfig) {
        if (!config) return undefined;
        const cfg = config as any;
        return cfg.apiRequest
            ? cfg.apiRequest as ApiRequestSettings
            : undefined;
    }

    constructor(config: AxiosRequestConfig) {
        const cfg = config as any;
        cfg.apiRequest = this;
    }
}

interface ApiRequestProgress {
    total: number;
    loaded: number;
}

class State {
    key?: OAuthKey;
    sys?: SabrSysInfo;
    authenticated: boolean;
    accessLevel: string;
    features: LicenseFeatures;
    unreachable: boolean;       // true when we should be connected but requests are failing
    lastError?: string;
    VODLicensePresent: boolean;
    VODOnlyLicensePresent: boolean;
    DAILicensePresent: boolean;
    QoELicensePresent: boolean;
    Licensed: boolean;
    idleTimeout: number;
    loadingMessage?: string;
    progress?: ApiRequestProgress;
}

interface Props extends OAuthCredentials {
    onAuthChange?: (status: OAuthClientStatus) => void;
    onAuthExpired?: (status: OAuthClientStatus) => void;
}

export default class OAuthClient extends React.Component<Props, State> {
    public static storage ?: Storage = window.sessionStorage;    // can set to window.localStorage or window.sessionStorage

    public static defaultProps: Partial<Props> = {
        scope: OAuthScope.WebUser
    };

    public static Context = OAuthContext.Consumer;
    public static ClientStatus = OAuthClientStatus;

    public attempts: number;
    private pending?: Promise<OAuthKey>;

    /* This view will only show conditionally based on user being authenticated or not
       attributes:
            authenticated:boolean - only show if user is authenticated
            login:boolean - show when the user is not logged in (i.e. a login view)
    */
    public static View = (props: any) => {
        return (
            <OAuthContext.Consumer>
                {(userInfo: OAuthClientStatus) => {
                    const enabled = (props.authenticated && userInfo.authenticated) ||
                        (props.login && !userInfo.authenticated);
                    return enabled
                        ? props.children
                        : null;
                }}
            </OAuthContext.Consumer>
        );
    }

    public static RequiresLdap = (props: any) => {
        return (
            <OAuthContext.Consumer>
                {(userInfo: OAuthClientStatus) => {
                    return (userInfo.ldapEnabled)
                        ? props.children
                        : null;
                }}
            </OAuthContext.Consumer>
        );
    }

    public static LdapDisabled = (props: any) => {
        return (
            <OAuthContext.Consumer>
                {(userInfo: OAuthClientStatus) => {
                    return (!userInfo.ldapEnabled)
                        ? props.children
                        : null;
                }}
            </OAuthContext.Consumer>
        );
    }

    public static RequiresAdmin = (props: any) => {
        return (
            <OAuthContext.Consumer>
                {(userInfo: OAuthClientStatus) => {
                    return (userInfo.level === 'admin')
                        ? props.children
                        : null;
                }}
            </OAuthContext.Consumer>
        );
    }

    public static Feature = (props: any) => {
        return (
            <OAuthContext.Consumer>
                {(userInfo: OAuthClientStatus) => {
                    const enabled = userInfo.features && Object.keys(props).reduce(
                        (acc: boolean, cur: string) => {
                            return (cur === 'children')
                                ? acc  // skip children prop
                                : acc && userInfo.features![cur];
                        },
                        true );
                    return enabled
                        ? props.children
                        : null;
                }}
            </OAuthContext.Consumer>
        );
    }

    static getContextFromResponseObject(response: any): ApiRequestContext {
        const { oauth, key } = ApiRequestSettings.from(response.config) || { oauth: undefined, key: undefined };
        return { oauth, key };
    }

    constructor(props: Props) {
        super(props);

        this.attempts = 0;
        this.interceptAxiosForAuthentication();

        const data = this.restoreFromStorage();
        this.state = {
            authenticated: false,
            sys: BlankSabrSysInfo,
            features: DefaultLicenseFeatures,
            VODLicensePresent: false,
            VODOnlyLicensePresent: false,
            DAILicensePresent: false,
            QoELicensePresent: false,
            Licensed: false,
            accessLevel: 'admin',
            idleTimeout: defaultIdleTimeout,
            unreachable: false,
            ...data
        };
    }

    public saveToStorage() {
        if (!OAuthClient.storage) {
            // tslint:disable-next-line:no-console
            console.log('OAuth storage disabled');
            return false;
        }

        const { key, VODLicensePresent, VODOnlyLicensePresent, DAILicensePresent, accessLevel, QoELicensePresent, Licensed } = this.state;
        OAuthClient.storage.setItem(
            'oauth',
            JSON.stringify( {
                key,
                VODLicensePresent,
                VODOnlyLicensePresent,
                DAILicensePresent,
                accessLevel,
                QoELicensePresent,
                Licensed
            })
        );
        return true;
    }

    public clearStorage() {
        if (!OAuthClient.storage) {
            // tslint:disable-next-line:no-console
            console.log('OAuth storage disabled');
            return false;
        }
        OAuthClient.storage.removeItem('oauth');
        return true;
    }

    public restoreFromStorage(): State | undefined {
        if (!OAuthClient.storage) {
            // tslint:disable-next-line:no-console
            console.log('OAuth storage disabled');
            return undefined;
        }

        // retrieve the oauth information as a string from session storage
        let dataStr = OAuthClient.storage.getItem('oauth');
        let data = dataStr && JSON.parse(dataStr);
        if (data && data.key) {
            // move the data to the state
            let { key, VODLicensePresent, VODOnlyLicensePresent, DAILicensePresent, accessLevel, QoELicensePresent, Licensed } = data;
            const tsnow = Math.floor(Date.now() / 1000);

            // if key is expired then delete it and return without restoring
            if (key === undefined || key.expiresAt < tsnow) {
                this.clearStorage();
                return undefined;
            }
            
            // update key's class prototype to add member functions
            key = Object.setPrototypeOf(key, OAuthKey.prototype);

            // if we have any keys, we can restore it and test it for validity
            if (key) {
                // we can return this restored key state, but we must also queue up a validity check
                let state: State = {
                    key,
                    sys: { ...BlankSabrSysInfo },
                    authenticated: true,
                    accessLevel,
                    features: { ... DefaultLicenseFeatures },
                    unreachable: false,
                    VODLicensePresent,
                    VODOnlyLicensePresent,
                    DAILicensePresent,
                    QoELicensePresent,
                    Licensed,
                    idleTimeout: (this.state && this.state.idleTimeout) || defaultIdleTimeout
                };

                // SAN-2389 now use the key to check if it is still valid (possible manual expiration or server restarted)
                // we query licenses as a way to validate the key
                this.checkLicense(key)
                    .then(
                        () => this.queryStatus(key).then(
                            // now set the refresh timer for this restored key
                            () => { this.setKeyRefreshTimer(key); }
                        )
                    )    // update remote status
                    .catch((err) => {
                        // tslint:disable-next-line:no-console
                        console.log(err);
                    });
                return state;
            }
        }
        return undefined;
    }

    public isValidCredentials(credentials: OAuthCredentials): boolean {
        return (
            credentials.host !== undefined &&
            credentials.clientID !== undefined &&
            credentials.clientSecret !== undefined
        );
    }

    public computeBasicHash(username: string, password: string) {
        return new Buffer(username + ':' + password).toString('base64');
    }

    public computeBasicHashForChangedPwd(username: string, oldPassword: string, newPassword: string) {
        return new Buffer(username + ':' + oldPassword + ':' + newPassword).toString('base64');
    }

    public signOut() {
        if (this.state.key)
            this.state.key.expire();
        
        this.setState( (state) => ({
                // clear all authentication
                ...state,
                authenticated: false,
                unreachable: false,
                key: undefined,
                sys: undefined,
                DAILicensePresent: false,
                VODLicensePresent: false,
                VODOnlyLicensePresent: false,
                QoELicensePresent: false,
                Licensed: false
        }),            () => {
            // when state is set, notify owners that auth has expired
            this.clearStorage();    // remove remembered oauth
            // tslint:disable-next-line:no-unused-expression
            this.props.onAuthExpired && this.props.onAuthExpired(this.getAuthStatus());
        });
    }

    public authenticateExisting(key?: OAuthKey | null): Promise<OAuthKey> {
        return new Promise<OAuthKey>((resolve, reject) => {
            if (this.attempts > 10)
                reject({ message: 'too many attempts'});

            if ((key === undefined) && this.state.key)
                key = this.state.key;

            if (!key) {
                // no key found, can't authenticate with existing connection
                reject({message: 'no existing host connection'});
            } else if (this.pending) {
                // we are authenticating now, so join the wait
                Promise.all([this.pending])
                    .then(() => {
                        if (key) resolve(key);
                    })
                    .catch(() => {
                        reject({message: 'authentication request failed'});
                    });
            } else if (key && key.expiring()) {
                // we must re-authenticate, if credentials were saved with key we can
                if (key.authorization) {
                    // expire the key
                    if (this.attempts < retryAttempts) {
                        this.refreshKey(key, true)
                            .then((k) => resolve(k))
                            .catch((e) => reject(e));
                    } else {
                        reject({
                            timestamp: new Date().valueOf(),
                            message: 'too many attempts'
                        });
                    }
                } else {
                    reject({
                        timestamp: new Date().valueOf(),
                        message: 'key has expired and no existing credentials'
                    });
                }
            } else if (key) {
                // key already exists and is good
                resolve(key);
            }
        });
    }

    setKeyRefreshTimer(key: OAuthKey, seconds?: number) {
        key.setRefreshTimer(this.refreshKey.bind(this), seconds);
    }

    public refreshKey(key: OAuthKey, blockOtherRequests: boolean = true) {
        if (!key) {
            if (this.state.key)
                key = this.state.key;
            else return new Promise<OAuthKey>( (resolve, reject) => {   // fake promise that always rejects
                reject('no key available');
                this.signOut();
            });
        }

        if (key.authorization === undefined) {
            // no way to re-authenticate
            return new Promise<OAuthKey>((resolve, reject) => {   // fake promise that always rejects
                reject('cannot refresh token, no credentials saved with key');
                this.signOut();
            });
        }

        // reset the key timestamp and expiresAt so we are sure we wont get stuck in a refresh loop
        // if refresh request doesnt go exactly as expected
        key.timestamp = Math.floor(Date.now() / 1000);
        key.expiresAt = key.timestamp + refreshBeforeExpiry + 300;    // if all else fails we wont cause another expirey/timeout for 5 mins

        let promise = new Promise<OAuthKey>( (resolve, reject) => {
            axios({
                method: 'post',
                url: (key.secureHost ? 'https' : 'http') + '://' + key.host + accessTokenUrl,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                    'Authorization': key.authorization
                },
                data: qs.stringify({
                    grant_type: 'client_credentials',
                    scope: key.scope
                })
            })
                .then((response) => {
                    if (response && response.data) {
                        const {access_token, token_type} = response.data;
                        if (token_type && token_type === 'Bearer' && access_token) {
                            key.bearer = access_token;
                            const expiresIn = Number(response.data.expires_in);
                            if (!isNaN(expiresIn) && expiresIn > 0) {
                                key.expiresAt = key.timestamp + expiresIn;
                                this.setKeyRefreshTimer(key, expiresIn - refreshBeforeExpiry);
                            }
                            key.refreshes = ++key.refreshes || 1;
                            this.attempts = 0;
                            if (this.pending === promise)
                                this.pending = undefined;

                            // query for sys status
                            this.queryStatus()    // update remote status
                                .then( () => this.checkLicense(key, false) )
                                .catch((err) => {
                                    // tslint:disable-next-line:no-console
                                    console.error('error querying remote status: ', err);
                                });

                            resolve(key);
                        }
                    } else {
                        // failed to referesh bearer so we better sign out for safety
                        this.signOut();
                        if (this.pending === promise)
                            this.pending = undefined;
                        reject({
                            message: 'Invalid user name or password'
                        });
                    }
                })
                .catch((error) => {
                    // tslint:disable-next-line:no-console
                    console.error('failed to refresh bearer token, ', error);
                    this.signOut();
                    if (this.pending === promise)
                        this.pending = undefined;
                    reject({ message: error} as OAuthError);
                });
        });
        if (blockOtherRequests)
            this.pending = promise;
        return promise;
    }

    public authenticate(credentials?: OAuthCredentials | string): Promise<OAuthKey> {
        if (credentials === undefined)
            return this.authenticateExisting();

        let host = (typeof credentials === 'string')
            ? credentials
            : credentials.host || window.location.hostname;

        // tslint:disable-next-line:no-console
        console.log('Login: host: ' + host);

        let key = this.state.key;
        this.attempts++;
        if (key || (typeof credentials === 'string'))
            return this.authenticateExisting(  key  );

        // new key
        const timestamp = Math.floor(Date.now() / 1000);
        key = new OAuthKey();
        key.host = host;
        key.timestamp = timestamp;

        // we are either authenticating for the first time, or the key is expired or about to
        this.pending = new Promise<OAuthKey>((resolve, reject) => {
            if (this.attempts > retryAttempts) {
                this.pending = undefined;
                reject({message: 'too many attempts'});
            }

            // validate credentials
            if (!this.isValidCredentials(credentials)) {
                this.pending = undefined;
                reject({
                    message: 'Invalid user name or password'
                });
            }
            if (key) {
                key.authorization = `Basic ${this.computeBasicHash(credentials.clientID, credentials.clientSecret)}`;

                // perform the authentication request
                axios({
                    method: 'post',
                    url: (credentials.secureHost ? 'https' : 'http') + '://' + credentials.host + accessTokenUrl,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                        'Authorization': key.authorization
                    },
                    data: qs.stringify({
                        grant_type: 'client_credentials',
                        scope: credentials.scope
                    })
                })
                    .then((response) => {
                        if (key && response && response.data) {
                            const {access_token, token_type} = response.data;
                            if (response.data.token_type && response.data.token_type === 'Bearer' && token_type && token_type === 'Bearer') {
                                key.bearer = access_token;
                                key.secureHost = credentials.secureHost;
                                key.clientID = new Buffer(credentials.clientID).toString('base64');
                                key.clientSecret = new Buffer(credentials.clientSecret).toString('base64');
                                key.scope = credentials.scope;
                                key.ldapEnabled = key.isLdapEnabled();

                                const expiresIn = Number(response.data.expires_in);
                                if (!isNaN(expiresIn) && expiresIn > 0) {
                                    key.expiresAt = key.timestamp + expiresIn;
                                    this.setKeyRefreshTimer(key, expiresIn - refreshBeforeExpiry);
                                }
                                this.pending = undefined;
                                // key.features = DefaultLicenseFeatures;
                                this.attempts = 0;
                                this.saveToStorage();
                                this.setState(prevState => ({
                                    ...prevState,
                                    key
                                }),           () => {
                                    // query for sys status
                                    this.queryStatus(key)    // update remote status
                                        .then(() => {
                                            // key gets set when status returns
                                            /*this.setState((prevState) => ({
                                                ...prevState,
                                                key: key
                                            }));*/
                                            resolve(key);
                                        })
                                        .catch((err) => {
                                            // tslint:disable-next-line:no-console
                                            console.error('server returned unexpected response: ', err);
                                            reject({ message: 'Server returned unexpected status response' } as OAuthError);
                                        });
                                });
                            }
                        } else {
                            this.pending = undefined;
                            const responseString = 'response';
                            const msg = response && response[responseString] && response[responseString].data;
                            reject({
                                message: (msg === undefined)
                                    ? 'Server not responding'
                                    : msg.error || 'Invalid user name or password'
                            });
                        }
                    })
                    .catch((error) => {
                        this.pending = undefined;
                        reject({message: error} as OAuthError);
                    });
            }
            return false;
        });

        return this.pending;
    }

    receiveSysStatus(sysStatusResponse: any, key: OAuthKey) {
        const {
            name, tzoffset, tz, httpInactivityTimeout, ver, loc, installDate, apiVersion,
            tunecount, schedcount, scancount, scanningStatus, scheduleStatus,
            vod, activeStreams, activePlaylists, licenseFlowLimit, uptime, scanActive, scanGroup
        } = sysStatusResponse;

        // SAN-4311
        this.setState((prevState) => ({
            Licensed: licenseFlowLimit === 1 ? false : true
        }));

        let sys: SabrSysInfo = {
            name,
            tzoffset,
            tz,
            version: ver,
            location: loc,
            activeStreams: activeStreams,
            activePlaylists: activePlaylists,
            licenseFlowLimit: licenseFlowLimit,
            uptime,
            installDate,
            apiVersion,
            interface: (vod && (vod.mode === 'continuous' || vod.active)) ? 'vod' : 'live',
            tuneCount: tunecount,
            scanning: {
                status: scanningStatus,
                group: scanActive === true ? scanGroup !== undefined ? scanGroup : 'NA' : 'NA',
                active: scanningStatus !== 'idle',
                count: scancount
            },
            schedule: {
                status: scheduleStatus,
                group: '',
                active: scheduleStatus !== 'idle',
                count: schedcount
            },
            vod: vod || {
                // default vod status if server doesnt support VOD status in sys/status
                enabled: false,
                active: false,
                mode: 'disabled',
                streams: 0,
                completed: 0,
                cycles: 0
            }
        };
        let idleTimeout = httpInactivityTimeout
            ? httpInactivityTimeout * 60000
            : defaultIdleTimeout;

        // check if a change occurred before causing a state change
        if (!_.isEqual(this.state.sys, sys) || this.state.idleTimeout !== idleTimeout) {
            // state has changed
            this.setState(prevState => ({
                ...prevState,
                sys,
                idleTimeout
            }));
            return sys;             // return new sysinfo
        } else
            return this.state.sys;  // return existing reference
    }

    setIdleTimeout(millis: number) {
        this.setState(prevState => ({
            ...prevState,
            idleTimeout: millis
        }));
    }

    /* make a call to a remote Sabr server to determine it is still there by calling sys/status */
    queryStatus(key?: OAuthKey) {
        return new Promise<SabrSysInfo>((resolve, reject) => {
            if (!key)
                key = this.state.key;
            
            if (!key) {
                reject('no key given and no default key available');
                return;
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${key.bearer}`
                }
            };

            // retrieve system information and some global parameters
            axios.get(key.resolveUrl('/api/sys/status'), config)
                .then((resp: AxiosResponse) => {
                    if (resp.data !== undefined) {
                        resolve(
                            this.receiveSysStatus(resp.data, key!)
                        );
                    } else {
                        reject(resp);
                    }
                })
                .catch((err: AxiosRequestConfig) => {
                    reject(err);
                });
        });
    }

    /* queries the remote system for license information.
       Returns a promise which will resolve or error when the asynchronous query operation completes. This is
       also used to verify that we have a valid OAuth bearer token.
     */
    queryLicense(key?: OAuthKey) {
        return new Promise<LicenseFeatures>((resolve, reject) => {
            if (!key)
                key = this.state.key;

            if (!key) {
                reject('no key given and no default key available');
                return;
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${key.bearer}`
                }
            };

            // make the license check
            axios.get(key.resolveUrl('/api/sys/license'), config)
                .then((resp: AxiosResponse) => {
                    if (resp.data.licenses !== undefined) {
                        let features: LicenseFeatures = {
                            DAI: !! _.find(resp.data.licenses, { 'name': 'SurveyorABRDAI' }),
                            VOD: !! _.find(resp.data.licenses, { 'name': 'SurveyorABRVOD' }),
                            VODOnly: !! _.find(resp.data.licenses, { 'name': 'SurveyorABRVODOnly' }),
                            Capture: !! _.find(resp.data.licenses, { 'name': 'SurveyorABRScanTrigCapRVL' }),
                            DataExtract: !! _.find(resp.data.licenses, { 'name': 'SurveyorABRDataExtract' }),
                            // tslint:disable-next-line:max-line-length
                            StreamPack: !! (_.find(resp.data.licenses, { 'name': 'SurveyorABRVS50StreamPack' })  || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS100StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS150StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS200StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS250StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS300StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS350StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS400StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS450StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS500StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorEABRMon10' }) || _.find(resp.data.licenses, { 'name': 'SurveyorCABRMon10' }) || _.find(resp.data.licenses, { 'name': 'SurveyorCABRMon50' }) || _.find(resp.data.licenses, { 'name': 'SurveyorCABRMon100' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon50' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon100' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon150' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon200' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon250' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon300' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon350' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon400' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon450' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon500' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon300M250' })),
                            // tslint:disable-next-line:max-line-length
                            QoE: !! (_.find(resp.data.licenses, { 'name': 'SurveyorABRQoE' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE50' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE100' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE150' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE200' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE250' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE300' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE350' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE400' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE450' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRQoE500' })),
                            level: resp.data.level,
                            Licensed: this.state.Licensed
                            // tslint:disable-next-line:max-line-length
                            // Licensed: !! (_.find(resp.data.licenses, { 'name': 'SurveyorABRVS50StreamPack' })  || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS100StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS150StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS200StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS250StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS300StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS350StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS400StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS450StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRVS500StreamPack' }) || _.find(resp.data.licenses, { 'name': 'SurveyorEABRMon10' }) || _.find(resp.data.licenses, { 'name': 'SurveyorCABRMon10' }) || _.find(resp.data.licenses, { 'name': 'SurveyorCABRMon50' }) || _.find(resp.data.licenses, { 'name': 'SurveyorCABRMon100' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon50' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon100' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon150' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon200' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon250' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon300' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon350' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon400' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon450' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon500' }) || _.find(resp.data.licenses, { 'name': 'SurveyorABRMon300M250' })),
                        };
                        // SAN-4528
                        if (features.VOD === true && features.VODOnly === true) {
                            // tslint:disable-next-line:no-console
                            console.log('*** Probe has both a VOD and a VODOnly license.');
                            features.VODOnly = false;
                        }
                        resolve(features);
                    } else {
                        reject('expected system licenses, got ' + resp.data);
                    }
                })
                .catch( (err: AxiosRequestConfig) => {
                    reject('expected system licenses, got' + err);
                });
        });
    }

    /* queries the remote system for license information and sets state on the current OAuth status
    */
    checkLicense(key?: OAuthKey, blockOtherRequests: boolean = true) {
        if (!key) {
            if (this.state.key)
                key = this.state.key;
            else return new Promise<OAuthKey>( (resolve, reject) => {   // fake promise that always rejects
                reject('no key available');
                this.signOut();
            });
        }

        let promise = new Promise<OAuthKey>( (resolve, reject) => {
            this.queryLicense(key)
                .then((features: LicenseFeatures) => {
                    if (key) {
                        this.setState((state) => ({
                            ...state,
                            features,
                            VODLicensePresent: features.VOD,
                            VODOnlyLicensePresent: features.VODOnly,
                            DAILicensePresent: features.DAI,
                            accessLevel: features.level,
                            QoELicensePresent: features.QoE,
                            Licensed: features.Licensed
                        }));
                        if (key && this.pending === promise)
                            this.pending = undefined;
                        this.saveToStorage();   // only when license is checked can we save to storage
                        resolve(key);
                    } else
                        reject('no key available');
                })
                .catch(err => {
                    if (this.pending === promise)
                        this.pending = undefined;
                    reject(err);
                    this.signOut();     // clear logout
                });
        });
        if (blockOtherRequests && this.pending === undefined)
            this.pending = promise;
        return promise;
    }

    /* Show a spinner and ping the remote server at regular intervals until it becomes ready.
       This is used to lock the user interface while the remote server is performing a reboot or install.
       Returns remote system information on success.
     */
    waitforServer(options ?: WaitforOptions, key?: OAuthKey): Promise<SabrSysInfo> {
        const opts: WaitforOptions = { ...DefaultWaitforOptions, ...options };
        if (!key) {
            if (this.state.key)
                key = this.state.key;
            else return new Promise<SabrSysInfo>( (resolve, reject) => {   // fake promise that always rejects
                reject('no key available');
                this.signOut();
            });
        }

        if (opts.message) {
            // if we are to show a popup, then call waitForServer recursively (with message cleared out)
            // this makes the code cleaner as we seperate the visual code from the background ping code.
            // We chain a promise here so we can control visuals before and after the recursive waitForServer returns
            return new Promise<SabrSysInfo>((resolve, reject) => {
                if (opts.message) {
                    // show the loading message
                    const backendOptions: WaitforOptions = {...opts, message: undefined};
                    const setMessage = (msg?: string) => this.setState((prevState) => { return {...prevState, loadingMessage: msg}; });

                    // show the spinner message
                    setMessage(opts.message);

                    // recurse to perform backend ping(), on complete clear the spinner and pass through resolve/reject
                    this.waitforServer(backendOptions, key)
                        .then((status: SabrSysInfo) => {
                            setMessage();   // clear spinner
                            resolve(status);
                        })
                        .catch((err: any) => {
                            setMessage();   // clear spinner
                            reject(err);
                        });
                }
            });
        }

        // if we get here, then we arent concerned with controlling visuals, this code is pure javascript pinging
        // on intervals until the server responds with success or 401 Unauthorized which typically indicates the server
        // restarted successfully and we were redirected back to login.

        // return a promise that repeatidly calls queryStatus()
        return new Promise<SabrSysInfo>((resolve, reject) => {
            // first call is typically a shorter backoff period just long enough to ensure the remote operation started
            const _this = this;

            // declare a ping() function we can reuse
            // if you think this would be cleaner as an anonymous function then be aware this is recursive, so you might
            // want to read https://stackoverflow.com/questions/3883780/javascript-recursive-anonymous-function
            function _ping() {
                _this.queryStatus(key)
                    .then( status => {
                        return resolve(status);
                    })
                    .catch((error) => {
                        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                            // server responded and must have restarted, so reject (even though this might be what caller expected)
                            reject({
                                message: 'server rebooted',
                                code: error.response.status
                            });
                            _this.signOut();    // guard call, should already be called by interceptor
                        } else {
                            // wait interval time and ping again
                            if (opts && opts.maxAttempts !== undefined && opts.maxAttempts-- === 0) {
                                reject({
                                    message: 'server timed out',
                                    code: 0
                                });
                            } else {
                                setTimeout(_ping, opts.checkInterval);
                            }
                        }
                    });
            }

            // start initial timeout
            setTimeout(_ping, opts.initialDelay);
        });
    }

    getAuthStatus(): OAuthClientStatus {
        const { key, sys, features, lastError, authenticated, VODLicensePresent, VODOnlyLicensePresent, DAILicensePresent, accessLevel, QoELicensePresent, Licensed } = this.state;

        let clientinfo: OAuthClientStatus;
        clientinfo = new OAuthClientStatus(this);
        clientinfo.DAILicensePresent = DAILicensePresent;
        clientinfo.VODLicensePresent = VODLicensePresent;
        clientinfo.VODOnlyLicensePresent = VODOnlyLicensePresent;
        clientinfo.QoELicensePresent = QoELicensePresent;
        clientinfo.Licensed = Licensed;
        clientinfo.level = accessLevel;
        clientinfo.features = DefaultLicenseFeatures;
        if (key) {
            clientinfo.key = key;
            clientinfo.host = key.host || 'localhost';
            clientinfo.secureHost = key.secureHost;
            clientinfo.clientID = key.clientID;
            clientinfo.scope = key.scope;
            clientinfo.authenticated = authenticated;
            clientinfo.features = features;
            clientinfo.sys = sys || BlankSabrSysInfo;
            clientinfo.clientSecret = key.clientSecret;
            clientinfo.ldapEnabled = key.ldapEnabled;
        }
        if (lastError)
            clientinfo.loginError = lastError;
        return clientinfo;
    }

    signInUsingClientCredentials(username: string, password: string) {
        let { host, secureHost, scope } = this.props;
        const creds = { clientID: username, clientSecret: password, clientNew: '', host, secureHost, scope };
        return new Promise((resolve, reject) => {
            if ((!this.attempts || this.attempts < 10) && this.isValidCredentials(creds)) {
                this.authenticate(creds)
                    .then((key) => {
                        this.checkLicense(key)
                            .then(() => {
                                this.setState( (prevState) => ({
                                    ...prevState,
                                    authenticated: true,
                                    lastError: undefined
                                }),            () => {
                                    const status = this.getAuthStatus();
                                    if (this.props.onAuthChange)
                                        this.props.onAuthChange(status);
                                    resolve(status);
                                });
                            })
                            .catch((err) => {
                                reject('failed license check');
                            });
                    })
                    .catch((err) => {
                        this.setState(prevState => ({
                            ...prevState,
                            key: undefined,
                            authenticated: false,
                            lastError: err.message
                        }),           () => {
                            const status = this.getAuthStatus();
                            if (this.props.onAuthChange)
                                this.props.onAuthChange(status);
                            reject(status);
                        });
                    });
            }
        });
    }

    updateUserPwd(username: string, oldPassword: string, newPassword: string) {
        let { host, secureHost, scope } = this.props;
        const creds = { clientID: username, clientSecret: oldPassword, clientNew: newPassword, host, secureHost, scope };

        return new Promise((resolve, reject) => {
            let _this = this;
            let key = new OAuthKey();

            if (key) {
                key.authorization = `Basic ${this.computeBasicHashForChangedPwd(username, oldPassword, newPassword)}`;
                axios({
                    method: 'post',
                    url: (secureHost ? 'https' : 'http') + '://' + host + accessResetUrl,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                        'Authorization': key.authorization
                    },
                    data: qs.stringify({
                        grant_type: 'client_credentials',
                        scope: creds.scope
                    })
                })
                    .then(function (response: AxiosResponse) {
                        if (response.request.status < 400) {
                            // tslint:disable-next-line:no-console
                            console.log('updated expired password');
                            const status = _this.getAuthStatus();
                            resolve(status);
                        } else {
                            // tslint:disable-next-line:no-console
                            console.log('updated expired password, got: ' + response.request.statusText);
                            _this.pending = undefined;
                            const responseString = 'response';
                            const msg = response && response[responseString] && response[responseString].data;
                            reject({
                                loginError: (msg === undefined)
                                    ? 'Server not responding'
                                    : msg.error || 'Invalid user name or password'
                            } as OAuthClientStatus);
                        }
                    })
                    .catch((error) => {
                        this.pending = undefined;
                        reject({loginError: error} as OAuthClientStatus);
                    });
            }
            return false;
        });

        return this.pending;
    }

    signInUsingNewCredentials(username: string, password: string, newPassword: string) {
        let { host, secureHost, scope } = this.props;
        const creds = { clientID: username, clientSecret: password, clientNew: newPassword, host, secureHost, scope };
        return new Promise((resolve, reject) => {
            if ((!this.attempts || this.attempts < 10) && this.isValidCredentials(creds)) {
                this.authenticate(creds)
                    .then((key) => {
                        this.checkLicense(key)
                            .then(() => {
                                this.setState( (prevState) => ({
                                    ...prevState,
                                    authenticated: true,
                                    lastError: undefined
                                }),            () => {
                                    const status = this.getAuthStatus();
                                    if (this.props.onAuthChange)
                                        this.props.onAuthChange(status);
                                    resolve(status);
                                });
                            })
                            .catch((err) => {
                                reject('failed license check');
                            });
                    })
                    .catch((err) => {
                        this.setState(prevState => ({
                            ...prevState,
                            key: undefined,
                            authenticated: false,
                            lastError: err.message
                        }),           () => {
                            const status = this.getAuthStatus();
                            if (this.props.onAuthChange)
                                this.props.onAuthChange(status);
                            reject(status);
                        });
                    });
            }
        });
    }

    renderLoader() {
        const { loadingMessage, progress } = this.state;
        if (!loadingMessage) return null;

        const loader = (progress && progress.loaded && progress.total)
            ? <Progress total={progress.total} value={progress.loaded} precision={1} inverted progress>{loadingMessage}</Progress>
            : <Loader indeterminate content={loadingMessage} />;

        // tslint:disable-next-line:jsx-wrap-multiline
        return <Dimmer className="darker-style" active>
            {loader}
        </Dimmer>;
    }

    renderUnreachable() {
        // tslint:disable-next-line:jsx-wrap-multiline
        return <Dimmer className="darker-style" active>
            <Loader indeterminate>
                <h3>Server is unreachable</h3>
                Attempting to re-establish communication...
            </Loader>
            <div style={{ position: 'relative', top: 100 }}>
                <Button inverted color="red" onClick={() => this.signOut()}>Sign Out</Button>
            </div>
        </Dimmer>;
    }

    render() {
        const { key, unreachable, loadingMessage, idleTimeout } = this.state;
        return (
            <React.Fragment>
                {!unreachable && loadingMessage && this.renderLoader()}
                {unreachable && this.renderUnreachable()}
                { key && <DataRequest
                    auto
                    debounce={350}
                    interval={6000}
                    endpoint={key.resolveUrl('api/sys/status')}
                    response={(data) => this.receiveSysStatus(data, key)}
                />}
                <IdleTimer
                    timeout={Math.max(60000, idleTimeout)}
                    onIdle={() => { this.signOut(); }}
                >
                    <OAuthContext.Provider value={this.getAuthStatus()}>
                        {this.props.children}
                    </OAuthContext.Provider>
                </IdleTimer>
            </React.Fragment>
        );
    }

    private interceptAxiosForAuthentication() {
        // patch axios to intercept requests and add authentication header
        axios.interceptors.request.use(
            (config: AxiosRequestConfig) => {
                if (!config.url) return config;

                // parse the URL and attempt to find a Key for it
                try {
                    const { key } = this.state;
                    let url = new URL(config.url);
                    let updateUrl = false;

                    // we only intercept API calls
                    // at least ensure we dont intercept the OAuth requests themselves and cause an infinite loop
                    // we have to make an exception for direct license calls since this call is used to test keys and we dont want to touch it
                    if (url.host && url.pathname.startsWith('/api/') && (url.host === 'default' || url.pathname !== '/api/sys/license')) {

                        // all API requests must be of JSON type
                        config.headers['Content-Type'] = 'application/json';

                        if (key && (key.host === url.host || url.host === 'default')) {
                            let req = new ApiRequestSettings(config);
                            req.oauth = this;
                            req.key = key;

                            // check to see if caller is requesting a loading message
                            if (config.headers['x-meta-caption']) {
                                // move the caption string to the ApiRequestSettings and clear it from headings (for now it causes CORS issue)
                                req.loaderMessage = config.headers['x-meta-caption'];
                                delete config.headers['x-meta-caption'];

                                // enable the loader
                                this.setState((prevState) => {
                                    return {...prevState, loadingMessage: req.loaderMessage };
                                });

                                // allow progress to be tracked on this request since we are showing a loader
                                config.onUploadProgress =  e => {
                                    if (e.total > 512000)
                                        this.setState((prevState) => {
                                            return {...prevState, progress: { loaded: e.loaded, total: e.total }};
                                        });
                                };
                                config.onDownloadProgress =  e => {
                                    if (e.total > 512000)
                                        this.setState((prevState) => {
                                            return {...prevState, progress: { loaded: e.loaded, total: e.total }};
                                        });
                                };
                            }

                            if (key.secureHost) {
                                url.protocol = 'https:';
                                updateUrl = true;
                            }

                            // if host is default, swap it out for the actual default hostname which was
                            // set in the top-level OAuth component
                            if (url.host === 'default') {
                                url.host = key.host;
                                updateUrl = true;
                            }

                            if (updateUrl)
                                config.url = url.toString();

                            // grab authentication bearer, or re-authenticate if we are about to expire
                            return this.authenticateExisting(key)
                                .then((k) => {
                                    if (key) {
                                        // add key to request headers
                                        config.headers.Authorization = `Bearer ${key.bearer}`;
                                    }
                                    return Promise.resolve(config);
                                })
                                .catch( (k) => Promise.reject(config) );
                        }
                    }
                } catch (e) {
                    // tslint:disable-next-line:no-console
                    console.log('axios interceptor for Authentication failed ', e);
                    return config;
                }
                return config;
            },
            (error) => { return Promise.reject(error); }
        );

        axios.interceptors.response.use(
            (response: any) => {
                if (response) {
                    const req = ApiRequestSettings.from(response.config);
                    if (req) {
                        response.oauth = this;
                        if (req && req.loaderMessage && this.state.loadingMessage === req.loaderMessage) {
                            this.setState((prevState) => (
                                {...prevState, loadingMessage: undefined, progress: undefined, unreachable: false }
                            ));
                        }
                    }
                }
                return response;
            },
            (error) => {
                if (error.response) {
                    const req = ApiRequestSettings.from(error.response.config);
                    if (req && this.state.loadingMessage === req.loaderMessage) {
                        this.setState((prevState) => (
                            {...prevState, loadingMessage: undefined, progress: undefined, unreachable: false }
                        ));
                    }
                    // SAN-4816
                    if (error.response.status === 400) {
                        return error.response;
                    }
                    if (error.response.status === 401 && error.config && error.config.url) {
                        if (error.config.url.endsWith('/oauth/access')) {
                            // invalid username and/or password
                            this.setState((prevState) => ({...prevState, unreachable: false}));
                            setTimeout(() => this.signOut(), 1000);
                        } else {
                            // server returned Access Denied, must invalidate the key
                            this.setState((prevState) => ({...prevState, unreachable: true}));
                            setTimeout(() => this.signOut(), 1000);
                        }

                        // Related to SAN-2671, UI was not refreshing or deleting expired keys
                        // This shouldnt happen now that we use a timer to refresh a key before it expires. However
                        // it can happen if the server is restarted for example.
                        // We may be able to allow a refresh here but we cannot redo the failed axios request, so
                        // it's probably better to just trigger user to login again.
                        /*let key = this.keyFromURL(error.config.url);
                        if (key !== null) {
                            this.expireKey(key);
                        }*/
                    }
                } else if (this.state.authenticated && !this.state.unreachable && !axios.isCancel(error)) {
                    const { key } = this.state;
                    const url = new URL(error.config.url);

                    if (key && url.host === key.host) {
                        // as long as this wasnt caused by a cancelled request!
                        // ...we are not reaching the server, indicate the disconnected state
                        this.setState((prevState) => ({...prevState, unreachable: true}));

                        // initiate a process that will wait for the server to respond
                        this.waitforServer({maxAttempts: 10}, this.state.key)
                            .then((status: SabrSysInfo) => {
                                this.setState((prevState) => ({...prevState, unreachable: false}));
                            })
                            .catch((err: any) => {
                                // too many attempts, force sign-out
                                this.signOut();
                            });
                    }
                }
                return error;
            }
        );
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/api/oauth.tsx